import { camelCase, capitalize } from 'lodash';

import { fetchPlanCreditPerPrice } from '@/api/apiService';
import { useSubscriptionStore } from '@/stores/subscription';
import { PlanPricing } from '@/types/components';
import { SUBSCRIPTION_BENEFITS } from '@/utils/constants/subscription';
import { formatDateDotMonthDay } from '@/utils/dateFormatter.js';
import { formatNumberWithComma } from '@/utils/stringUtils.js';

export const usePlan = () => {
  const { t } = useI18n();

  const subscriptionStore = useSubscriptionStore();

  const formatBenefitTranslation = (benefit, planPricing?: PlanPricing): string => {
    if (benefit.type === 'param' || benefit.type === 'add') {
      return t(benefit.key, benefit.params);
    }

    if (benefit.type === 'api') {
      const includedCredit = planPricing?.KRW.creditIncluded ?? 0;
      return `${formatNumberWithComma(includedCredit)}${t(benefit.key)}`;
    }

    if (benefit.type === 'time') {
      const timeParts = [
        benefit.times.hours && `${benefit.times.hours}${t('label_hour')}`,
        benefit.times.minutes && `${benefit.times.minutes}${t('label_minute')}`,
      ].filter(Boolean);

      return `${t(benefit.key)} (~${timeParts.join(' ')})`;
    }

    return t(benefit.key);
  };

  const formatBenefitByCategory = (plan: string, planPricing?: PlanPricing) => {
    const result = {
      language: '',
      price: '',
      pricePerChat: [] as string[],
      features: [] as string[],
    };

    SUBSCRIPTION_BENEFITS[plan].forEach((benefit) => {
      const translated = formatBenefitTranslation(benefit, planPricing);

      if (benefit.type === 'param') {
        result.language = translated;
      } else if (['api', 'add'].includes(benefit.type)) {
        result.price = translated;
      } else if (benefit.type === 'time') {
        result.pricePerChat.push(translated);
      } else {
        result.features.push(translated);
      }
    });

    return result;
  };

  const getUserPlanCreditPerPrice = async (plan, currency) => {
    if (plan === 'ENTERPRISE') {
      return [];
    }

    try {
      const { data, success } = await fetchPlanCreditPerPrice();

      if (!success) {
        return [];
      }

      const ratio = data[plan].creditPerPriceRatios[currency].ratio;
      const list = data[plan].creditPerPriceRatios[currency].creditPerPrices;

      return [ratio, list];
    } catch (error) {
      console.error('플랜별 크레딧/가격 조회 실패: ', error);
      return [];
    }
  };

  const calcCeilPrice = (credit, currency, ratio) => {
    const CEIL_UNIT = currency === 'KRW' ? 10 : 100;
    const price = Number(credit) / ratio;

    if (currency === 'KRW') {
      const roundedPrice = Math.round(price);
      return Math.ceil(roundedPrice / CEIL_UNIT) * CEIL_UNIT;
    }

    const roundedPrice = Math.round(price * 1000) / 1000;
    return Math.ceil(roundedPrice * CEIL_UNIT) / CEIL_UNIT;
  };

  const formatCurrency = (price, currency) => {
    if (price === undefined || price === null) {
      return;
    }

    if (currency === 'KRW') {
      return `${formatNumberWithComma(price)}${t('label_currency_won')}`;
    }

    return `$${formatNumberWithComma(price)}`;
  };

  const getCreditDiscountLabel = (plan) => {
    //TODO: API로 부터 할인율이 전달되면, 해당 값 변경 필요
    const discount = plan === 'STARTER' ? 25 : 50;

    switch (plan) {
      case 'FREE':
        return t('credit_purchase_no_discount');
      case 'STARTER':
      case 'PRO':
        return t('credit_purchase_discount', { discount });
      default:
        return t('credit_purchase_discount_inquiry');
    }
  };

  const getPlanButtonText = (displayedPlan): string => {
    if (displayedPlan === 'ENTERPRISE') {
      return t('self_service_contact_submit');
    }

    const plan = subscriptionStore.currentPlan;
    const planStatus = subscriptionStore.currentPlanStatus;

    if (displayedPlan === plan) {
      return planStatus === 'CANCEL'
        ? t('continue_with_plan', { plan: capitalize(camelCase(plan)) })
        : t('currently_in_use');
    }

    if (displayedPlan === 'FREE' && ['STARTER', 'PRO'].includes(plan)) {
      return t('self_service_subscription_cancel');
    }

    if (planStatus === 'PLANNED' && subscriptionStore.plannedPlanDetails?.plan === displayedPlan) {
      const date = formatDateDotMonthDay(subscriptionStore.plannedPlanDetails?.startDate);

      return t('change_planned_day', { date });
    }

    return t('change_subscription');
  };

  const getPlanDisabledState = (displayedPlan) => {
    const planStatus = subscriptionStore.currentPlanStatus;

    return (
      (planStatus !== 'CANCEL' && displayedPlan === subscriptionStore.currentPlan) ||
      (planStatus === 'CANCEL' && displayedPlan === 'FREE') ||
      (planStatus === 'PLANNED' && displayedPlan === subscriptionStore.plannedPlanDetails?.plan)
    );
  };

  return {
    formatBenefitByCategory,
    getUserPlanCreditPerPrice,
    calcCeilPrice,
    formatCurrency,
    formatBenefitTranslation,
    getCreditDiscountLabel,
    getPlanButtonText,
    getPlanDisabledState,
  };
};
